import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/toast'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useDeleteCreditNote } from '@/hooks/useInvoices'
import { getApiErrorMessage } from '@/services/api'
import type { CreditNote } from '@/types'

interface Props {
  creditNote: CreditNote | null
  onClose: () => void
  onDeleted?: () => void
}

// Feedback #45: delete a Draft credit note with a confirmation guard. Only
// surfaced for draft rows (BE blocks editing/deleting a sent credit note).
// Figma 1336:31123.
export function DeleteCreditNoteDialog({
  creditNote,
  onClose,
  onDeleted,
}: Props) {
  const { t } = useTranslation('invoices')
  const del = useDeleteCreditNote()

  const confirm = async () => {
    if (!creditNote) return
    try {
      await del.mutateAsync(creditNote.id)
      toast({ variant: 'success', title: t('deleteCreditNote.success') })
      onDeleted?.()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('deleteCreditNote.error')),
      })
    }
  }

  return (
    <ConfirmDeleteDialog
      open={!!creditNote}
      onOpenChange={o => !o && onClose()}
      title={t('deleteCreditNote.title')}
      description={t('deleteCreditNote.confirmText')}
      confirmLabel={t('deleteCreditNote.confirm')}
      cancelLabel={t('deleteCreditNote.cancel')}
      onConfirm={confirm}
      busy={del.isPending}
    />
  )
}

export default DeleteCreditNoteDialog
